import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, NotebookPen, Scissors } from 'lucide-react';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn, humanize, timeOnly } from '@/lib/utils';
import { StatTile } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, StatusBadge, Textarea } from '@/components/ui/primitives';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { STATUS_FLOW, type OtCaseDetail } from './TheatrePage';

export function CasePage() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/ot');

  const detail = useQuery({
    queryKey: ['ot-case', caseId],
    queryFn: () => get<OtCaseDetail>(`/ot/cases/${caseId}`),
    enabled: Boolean(caseId),
  });

  if (!detail.data) {
    return (
      <FormScreen title="Case" icon={Scissors} backTo={returnTo}>
        <RecordState error={detail.error} missing={!detail.isLoading} label="Loading the case…" />
      </FormScreen>
    );
  }

  return <CaseForm key={detail.data.id} otCase={detail.data} returnTo={returnTo} />;
}

function CaseForm({ otCase, returnTo }: { otCase: OtCaseDetail; returnTo: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [operativeNotes, setOperativeNotes] = useState(otCase.operativeNotes ?? '');
  const [postOpNotes, setPostOpNotes] = useState(otCase.postOpNotes ?? '');

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => put(`/ot/cases/${otCase.id}`, body),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['ot-case', otCase.id] });
      void queryClient.invalidateQueries({ queryKey: ['ot-cases'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const advance = useMutation({
    mutationFn: (status: string) =>
      put(`/ot/cases/${otCase.id}`, {
        status,
        ...(operativeNotes ? { operativeNotes } : {}),
        ...(postOpNotes ? { postOpNotes } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ot-case', otCase.id] });
      void queryClient.invalidateQueries({ queryKey: ['ot-cases'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const nextStatus = STATUS_FLOW[STATUS_FLOW.indexOf(otCase.status) + 1];
  const dirty = !advance.isSuccess && (operativeNotes !== (otCase.operativeNotes ?? '') || postOpNotes !== (otCase.postOpNotes ?? ''));
  const done = otCase.whoChecklistItems.filter((i) => i.done).length;

  return (
    <FormScreen
      title={otCase.procedureName}
      description={`${otCase.patient.fullName} · ${otCase.patient.mrn} · ${otCase.theatre.name}`}
      eyebrow="Operation theatre"
      icon={Scissors}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!nextStatus) return;
        setError(null);
        advance.mutate(nextStatus);
      }}
    >
      {otCase.patient.allergies.length > 0 && (
        <Alert variant="destructive" title="Allergies">
          {otCase.patient.allergies.map((a) => a.substance).join(', ')}
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="Status" value={<StatusBadge status={otCase.status} />} />
        <StatTile label="Knife to skin" value={otCase.actualStart ? timeOnly(otCase.actualStart) : '—'} />
        <StatTile label="Out of theatre" value={otCase.actualEnd ? timeOnly(otCase.actualEnd) : '—'} />
      </div>

      <FormSection title={`WHO surgical safety checklist (${done}/${otCase.whoChecklistItems.length})`} icon={ClipboardCheck}>
        {otCase.whoChecklistItems.map((item) => (
          <Checkbox
            key={item.key}
            checked={item.done}
            disabled={update.isPending}
            onCheckedChange={(checked) => update.mutate({ whoChecklist: { [item.key]: checked } })}
            label={humanize(item.key)}
            className={cn('rounded-md border px-3 py-1.5', item.done ? 'border-success/40 bg-success/5' : 'border-border')}
          />
        ))}
        <Checkbox
          checked={Boolean(otCase.spongeCountCorrect)}
          disabled={update.isPending}
          onCheckedChange={(checked) => update.mutate({ spongeCountCorrect: checked })}
          label="Sponge count correct"
          className={cn('rounded-md border px-3 py-2', otCase.spongeCountCorrect ? 'border-success/40 bg-success/5' : 'border-warning/40 bg-warning/5')}
        />
        <Checkbox
          checked={Boolean(otCase.instrumentCountCorrect)}
          disabled={update.isPending}
          onCheckedChange={(checked) => update.mutate({ instrumentCountCorrect: checked })}
          label="Instrument count correct"
          className={cn('rounded-md border px-3 py-2', otCase.instrumentCountCorrect ? 'border-success/40 bg-success/5' : 'border-warning/40 bg-warning/5')}
        />
      </FormSection>

      <FormSection title="Notes" icon={NotebookPen} columns={1}>
        <Field label="Operative notes">
          <Textarea rows={4} value={operativeNotes} onChange={(e) => setOperativeNotes(e.target.value)} />
        </Field>
        <Field label="Post-op notes">
          <Textarea rows={3} value={postOpNotes} onChange={(e) => setPostOpNotes(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      {nextStatus && <FormActions submitLabel={`Move to ${humanize(nextStatus)}`} loading={advance.isPending} cancelLabel="Close" cancelTo={returnTo} />}
    </FormScreen>
  );
}
